import OpeningTimesTable from '@atoms/OpeningTimesTable/OpeningTimesTable';
import LocationSchema from '@atoms/schemas/locationSchema';
import {
  BG_ALTERNATIVE,
  BG_DEFAULT,
  EVENTS_ON_LOCATION,
  STATIC_MAP_SECTION_ID,
} from '@constants/constants';
import useLocale from '@hooks/useLocale';
import useTranslations from '@hooks/useTranslations';
import ContentCarouselSection from '@organisms/ContentCarouselSection/ContentCarouselSection';
import FacilitiesSection from '@organisms/FacilitiesSection/FacilitiesSection';
import Hero from '@organisms/Hero/Hero';
import HeroCtas from '@organisms/Hero/HeroCtas';
import HighlightedBannerSection from '@organisms/HighlightedBannerSection/HighlightedBannerSection';
import ImageSliderSection from '@organisms/ImageSliderSection/ImageSliderSection';
import MapSection from '@organisms/MapSection/MapSection';
import Sections from '@organisms/Sections/Sections';
import TextSection from '@organisms/TextSection/TextSection';
import VideoSection from '@organisms/VideoSection/VideoSection';
import { LocationPage as Props } from '@type-declarations/page';
import { getHeroColor } from '@utils/getHeroColor';
import { findHighlightedTag } from '@utils/getHighlightedTag';
import { useMemo } from 'react';

const translations = {
  events: {
    nl: 'Evenementen',
    en: 'Events',
  },
};

function LocationTemplate({ page }: { page: Props }) {
  const locale = useLocale();

  const {
    id,
    title,
    introText,
    image,
    phone,
    mail,
    website,
    ctaHero,
    coordinates,
    socials,
    address,
    items,
    alignment,
    category,
    tags,
    featuredLocations,
    facilities,
    descriptionIsCollapsible,
  } = page;
  const t = useTranslations();

  const heroColor = useMemo(() => getHeroColor({ page }), [page]);
  const highlightedTag = useMemo(() => findHighlightedTag(tags), [tags]);

  return (
    <>
      <Hero
        pageId={id}
        title={title}
        text={introText}
        image={image}
        colorVariant={heroColor}
        containerSize="narrow"
        showTopBar
        variant="small"
        alignment={alignment}
      >
        <HeroCtas
          cta={ctaHero}
          website={website}
          mapId={address && coordinates && STATIC_MAP_SECTION_ID}
        />
      </Hero>
      {page.description && (
        <TextSection
          id="location-description"
          typename="TextSection"
          text={page.description}
          cta={page.cta}
          backgroundColor={{ color: BG_DEFAULT }}
          descriptionIsCollapsible={descriptionIsCollapsible}
        />
      )}
      {!!page.openingHours?.regularHours.length && (
        <OpeningTimesTable
          regularHours={page.openingHours.regularHours}
          specialHours={page.openingHours.specialHours}
          message={page.openingHoursMessage}
        />
      )}

      <FacilitiesSection
        typename="FacilitiesSection"
        id="location-facilities-section"
        title={t.facilities}
        backgroundColor={{ color: BG_DEFAULT }}
        facilities={facilities}
      />

      {page.youtubeId && (
        <VideoSection
          id="location-video"
          typename="VideoSection"
          video={{ youtubeId: page.youtubeId }}
          backgroundColor={{ color: BG_DEFAULT }}
          fullWidth={false}
        />
      )}
      {page.extraImages && (
        <ImageSliderSection
          id="location-images"
          typename="ImageSliderSection"
          images={page.extraImages}
          backgroundColor={{ color: BG_DEFAULT }}
        />
      )}

      {highlightedTag && (
        <HighlightedBannerSection
          icon={highlightedTag.icon}
          bannerTitle={
            highlightedTag.bannerTitle || t.highlightedBannerSectionTitle
          }
          bannerText={
            highlightedTag.bannerText || t.highlightedBannerSectionText
          }
          theme={highlightedTag.theme}
          bannerCta={highlightedTag.bannerCta}
        />
      )}

      {!!items?.length && (
        <ContentCarouselSection
          title={translations.events[locale]}
          selection={page.items}
          typename="ContentCarouselSection"
          id={`location-events-${page.id}`}
          backgroundColor={{ color: BG_ALTERNATIVE }}
          maxCount={EVENTS_ON_LOCATION}
        />
      )}

      {address && coordinates && (
        <MapSection
          id={STATIC_MAP_SECTION_ID}
          isLocationPage
          typename="MapSection"
          size="smallFixed"
          title={t.contact}
          backgroundColor={{ color: BG_DEFAULT }}
          currentLocation={{
            id: `map-pin-${id}`,
            typename: 'LocationPage',
            title,
            icon: category?.[0]?.icon || category?.[0]?.category?.[0]?.icon,
            pos: [Number(coordinates.latitude), Number(coordinates.longitude)],
            address,
            image,
            slug: page.slug,
          }}
          locations={featuredLocations}
          information={{
            address,
            phone,
            mail,
            website,
            socials,
          }}
        />
      )}

      <Sections sections={page.sections} />

      <LocationSchema page={page} />
    </>
  );
}

export default LocationTemplate;
